// Pure BreadcrumbList JSON-LD builder for the region catch-all page (#150).
//
// Extracted from app/[[...slug]]/RegionPageClient.jsx so it can be unit-tested
// without pulling in the client render tree. Mirrors next-seo's BreadcrumbJsonLd
// output shape.
//
// #150 fix: on a COMMERCE page the canonical is region-prefixed (/us/product/…),
// but the crumbs used to be built from the bare baseUrl, so every commerce
// breadcrumb item pointed at a bare /product/… URL that 307-redirects to the
// region variant and mismatches the page's own canonical. Region-prefix the item
// URLs whenever the (leaf) slug is a commerce path and we know the region;
// content pages stay bare (their canonical is bare too). The Home crumb is always
// the bare baseUrl — "/" is a 200 content page, not a redirect.

import { isCommercePath, isRegion } from './regions'

// Human labels for known breadcrumb path segments. Naive title-case already
// handles most leaf slugs ("pro-pedals" -> "Pro Pedals"), but produces awkward
// crumbs for category segments ("product" -> "Product"); map those to nicer
// labels. Anything not listed falls back to title-case.
const BREADCRUMB_LABELS = {
  product: 'Products',
  hardware: 'Hardware',
  shop: 'Shop',
  store: 'Store',
  specs: 'Specs',
  // Title-case mangles the product name itself on these leaves ("gt2-cockpit"
  // -> "Gt2 Cockpit"), and the breadcrumb name is what Google prints under the
  // result URL, so spell the product the way the brand does.
  'gt2-cockpit': 'GT-2 Cockpit',
  'gt2-seat': 'GT-2 Seat',
  'gt2-full-rig-bundle': 'GT-2 Full Rig Bundle',
  // The GT2-TX32 slug still contains "stand" (renaming it would break a live URL
  // for a wording change), so title-casing printed "Triple Monitor Stand Tx32"
  // under the result — the retired name — after the product became a Mount.
  'gt2-triple-monitor-stand-tx32': 'Triple Monitor Mount (GT2-TX32)',
}

export function breadcrumbLabel(segment) {
  if (BREADCRUMB_LABELS[segment]) return BREADCRUMB_LABELS[segment]
  return String(segment)
    .split('-')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ')
}

export function breadcrumbJsonLd(slug, baseUrl, region) {
  if (!slug || !baseUrl) return null
  const parts = String(slug).split('/').filter(Boolean)
  if (parts.length === 0) return null

  const prefix =
    isCommercePath(slug) && isRegion(region) ? `/${String(region).toLowerCase()}` : ''

  const itemListElement = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: baseUrl,
    },
  ]
  let currentPath = ''
  parts.forEach((part, index) => {
    currentPath = `${currentPath}/${part}`
    itemListElement.push({
      '@type': 'ListItem',
      position: index + 2,
      name: breadcrumbLabel(part),
      item: `${baseUrl}${prefix}${currentPath}`,
    })
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  }
}
